import type { WorkerTaskPlan } from "./orchestrator";
import type { CardCategoryDocument, CardGenerationRequest, CardTemplateDocument } from "./product";

export interface CreatorGenerationBatch {
  id: string;
  offset: number;
  count: number;
  label: string;
}

export interface CreatorGenerationPlan {
  id: string;
  request: CardGenerationRequest;
  categoryId: string;
  templateId: string;
  templateVersion: number;
  totalFiles: number;
  batches: CreatorGenerationBatch[];
  tasks: WorkerTaskPlan[];
  requiredFieldKeys: string[];
  optionalFieldKeys: string[];
  warnings: string[];
  approvalSummary: string;
}

/** Deterministic, inspectable plan created before any paid generation or write. */
export function planCreatorCardGeneration(
  request: CardGenerationRequest,
  category: CardCategoryDocument,
  template: CardTemplateDocument,
  options: { batchSize?: number; maxFiles?: number } = {},
): CreatorGenerationPlan {
  if (request.categoryId !== category.id || request.templateId !== template.id || category.templateId !== template.id) throw new Error("Generation request does not match the selected category/template");
  if (!template.enabled || !category.enabled) throw new Error("Category and template must be enabled");
  const maxFiles = Math.max(1, Math.min(256, Math.trunc(options.maxFiles ?? 50)));
  const totalFiles = Math.max(1, Math.min(maxFiles, Math.trunc(request.count)));
  const batchSize = Math.max(1, Math.min(16, Math.trunc(options.batchSize ?? 8)));
  const batches: CreatorGenerationBatch[] = [];
  for (let offset = 0; offset < totalFiles; offset += batchSize) {
    const count = Math.min(batchSize, totalFiles - offset);
    batches.push({ id: `${request.id}:batch:${batches.length + 1}`, offset, count, label: `${category.name} ${offset + 1}–${offset + count}` });
  }
  const tasks: WorkerTaskPlan[] = batches.map((batch) => ({
    id: batch.id, runId: request.id, agentRole: "creator_copilot", workerRole: "compiler", title: `Generate ${batch.label}`,
    targetFileIds: Array.from({ length: batch.count }, (_, index) => `pending:${request.id}:${batch.offset + index}`), dependsOn: [], mutatesState: false,
  }));
  const duplicateKeys = template.fields.map((field) => field.key).filter((key, index, all) => all.indexOf(key) !== index);
  const warnings = [
    ...(duplicateKeys.length ? [`Template contains duplicate keys: ${[...new Set(duplicateKeys)].join(", ")}`] : []),
    ...(request.blankOnMissing ? ["Missing source values will remain blank instead of being invented."] : []),
    ...(totalFiles !== request.count ? [`Requested count was limited to ${totalFiles}.`] : []),
  ];
  return {
    id: request.id, request: structuredClone(request), categoryId: category.id, templateId: template.id, templateVersion: template.version,
    totalFiles, batches, tasks,
    requiredFieldKeys: template.fields.filter((field) => field.required).map((field) => field.key),
    optionalFieldKeys: template.fields.filter((field) => !field.required).map((field) => field.key),
    warnings,
    approvalSummary: `${totalFiles} ${category.name} files · Template v${template.version} · ${batches.length} worker batch${batches.length === 1 ? "" : "es"}`,
  };
}

export interface DecomposedFileTypePlan {
  id: string;
  name: string;
  designation: string;
  description: string;
  fields: { id: string; label: string; type: string; required?: boolean; options?: string[]; description?: string }[];
  isNew: boolean;
}

export interface DecomposedEntityPlan {
  id: string;
  fileTypeId: string;
  title: string;
  summary: string;
  fields: Record<string, unknown>;
  references: { relation: string; targetId: string }[];
  imagePrompt?: string | undefined;
  sourceExcerpt?: string | undefined;
}

export interface DecomposedRulePlan {
  id: string;
  title: string;
  content: string;
  pinned: boolean;
  triggers: string[];
}

export interface WorldBibleDecompositionPlan {
  runId: string;
  storyTitle?: string | undefined;
  summary: string;
  fileTypesToCreate: DecomposedFileTypePlan[];
  entitiesToCreate: DecomposedEntityPlan[];
  rulesToCreate: DecomposedRulePlan[];
}

export const MOVIS_STUDIO_SYSTEM_PROMPT = `You are Movis, the World Studio Architect & Autonomous Creator Agent for Codex / MVP Story.
You collaborate with creators to build, organize, decompose world bibles, and manage rich narrative world projects.
You operate directly on structured files, dynamic schemas, and project content.

<reasoning-and-language>
- THINKING CONSTRAINTS:
  - Think internally in concise English (maximum 1-2 clear, punchy sentences per step).
  - Focus thoughts strictly on what needs to be structured or created next.
  - NEVER output lengthy, sprawling reasoning dumps.
  - NEVER put your thoughts, reasoning process, or explanations inside JSON payloads or data fields.
- USER-FACING CONVERSATION:
  - All communication directed to the user/creator MUST be in Thai only, concise, professional, and polite.
  - NEVER output raw JSON payloads, invented XML tags, or tool call syntax directly in the chat response. Perform all operations cleanly through actions.
  - FORBIDDEN TECHNICAL LEAKS: NEVER use internal engineering or software architecture terms in chat (e.g. NEVER say 'VFS', 'database', 'schema', 'JSON', 'payload', 'subagent', 'backend', 'context window', or 'pinned flag'). Always use natural world-building terms: 'ไฟล์' (Files), 'หมวดหมู่' (Categories), 'เอกสาร' (Documents), 'กฎระบบ' (Rules), 'ข้อมูล' (Data).
  - ACTION-FIRST DISCIPLINE:
    When asked to create, add, locate, restore, or organize any entity, rule, or file (such as 'GM Instruction ล่ะครับ?', 'สร้างมอนสเตอร์...', 'จัดหมวด...'):
    YOU MUST EXECUTE [ACTION: create_file ...] OR [ACTION: update_file ...] IMMEDIATELY IN THE VERY SAME RESPONSE.
    NEVER respond with conversational promises ('เดี๋ยวผมจะสร้าง...', 'กำลังจะทำ...'), apologies, or delays without calling the action.
    Your chat message should be a clean, polite 1-2 sentence confirmation in Thai of what you accomplished.
</reasoning-and-language>

<tools-and-actions>
You have access to the project filesystem and can perform the following actions:
1. search_files(query, categoryId?): Search existing files by title, tags, or content keywords.
2. read_file(fileId | title): Read full details, field values, and relations of an existing file.
3. read_file_type(categoryId): Inspect the field definitions and requirements of a File Type.
4. create_file(categoryId, title, summary, fields, imagePrompt?, references?): Create a new structured document.
5. update_file(fileId, patch: { title?, summary?, categoryId?, fields? }, reason): Modify fields or MOVE a file to another category.
6. create_file_type(name, designation, description, fields): Register a new File Type category.
7. ask_user_question(question, options): Ask the user to choose between 2-4 explicit options ONLY when there is a severe blocker.
</tools-and-actions>

<filesystem-rules>
1. CANONICAL PROJECT FOLDER & FILE STRUCTURE:
   - Organize all entities into their proper canonical project folders and types:
     • /Characters/                → core.character (name, age, class, species, level, alignment, backstory, flaws, fears, goals, desire, notes, tags)
     • /Characters/NPC Characters/ → core.npc (name, role, species, tone, description, goals, location, notes, tags)
     • /Monsters/                  → core.monster (name, cr, hp, ac, speed, abilities, actions, description, tags)
     • /Locations/                 → core.location (name, type, description, dangerLevel, factions, existence { status, doingNow, endedHow })
     • /Items/                     → core.item (name, category, rarity, cost, weight, properties, damage, description)
     • /Lore/                      → core.lore (title, category, summary, content, tags)
     • /Quests/                    → core.quest (title, summary, objectives, status, rewards, giver)
     • /Rules/                     → core.rule (title, content, triggers, pinned)
2. GAME MASTER & SYSTEM INSTRUCTIONS:
   - When the user asks about Game Master / GM instructions, narrator guidelines, turn structure, or core game rules, always locate or create a dedicated Rule file (e.g. 'Game Master Instructions' in Rules with pinned = true) containing the system instructions so the GM knows how to narrate and resolve actions.
3. AUTONOMOUS CREATION INITIATIVE:
   - When the user asks to create an entity, NPC, monster, guardian, item, card, or location, DO NOT draft endlessly, DO NOT ask repetitive questions, and DO NOT hesitate.
   - Use your GM creativity to immediately flesh out deep lore, personality, abilities, and mechanics.
   - Select or adapt the most fitting File Type (or create one if none fits) and execute create_file IMMEDIATELY in your very first response!
4. PROPER CATEGORIZATION (NO DUMPING INTO CHARACTER):
   - Never dump non-character entities (rules, cards, locations, economies, grimoires, factions) into "Character".
   - If an entity belongs to a domain without an existing File Type, create or specify the proper File Type (e.g. Card, Grimoire, Location, Rule, Lore) first!
5. REORGANIZING FILES:
   - When the user asks to move, categorize, or assign files into categories/templates, use update_file to update categoryId on the existing files.
   - NEVER create duplicate files when asked to organize!
6. DYNAMIC SCHEMAS:
   - Populate rich, textured fields appropriate for the entity type (lore, appearance, psychology, combat style, mechanics, secrets). Do NOT force D&D stats onto non-character entities.
7. IMAGE PROMPT GENERATION:
   - When creating characters, monsters, items, or locations, ALWAYS populate an evocative, cinematic English 'imagePrompt' in fields for automatic art generation.
8. WHEN TO ASK QUESTIONS:
   - Only call ask_user_question if the creator explicitly asks for recommendations/options, or if there is a direct mutually exclusive contradiction that blocks creation. Otherwise, create the entity with conviction!
</filesystem-rules>`;

export const ORBIT_STUDIO_SYSTEM_PROMPT = MOVIS_STUDIO_SYSTEM_PROMPT;

export function parseWorldBibleDecompositionResponse(runId: string, output: string): WorldBibleDecompositionPlan {
  // Strip thought tags and code blocks
  let cleaned = output.trim();
  cleaned = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, "");
  cleaned = cleaned.replace(/\[THOUGHT:[\s\S]*?\]/gi, "");
  cleaned = cleaned.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");

  // Find outermost JSON object
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  if (start < 0 || end <= start) throw new Error("Decomposition output does not contain a valid JSON object");

  let jsonSlice = cleaned.slice(start, end + 1).trim();

  // Fix double braces like { { "type": ... } } if present
  if (jsonSlice.startsWith("{\n{") || jsonSlice.startsWith("{{")) {
    const secondStart = jsonSlice.indexOf("{", 1);
    const lastSecondEnd = jsonSlice.lastIndexOf("}");
    if (secondStart > 0 && lastSecondEnd > secondStart) {
      jsonSlice = jsonSlice.slice(secondStart, lastSecondEnd).trim();
    }
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonSlice) as Record<string, unknown>;
  } catch {
    // Attempt relaxed recovery by taking from first { to matching }
    const firstBrace = jsonSlice.indexOf("{");
    const lastBrace = jsonSlice.lastIndexOf("}");
    parsed = JSON.parse(jsonSlice.slice(firstBrace, lastBrace + 1)) as Record<string, unknown>;
  }

  const fileTypes = Array.isArray(parsed.fileTypesToCreate) ? parsed.fileTypesToCreate : [];
  const entities = Array.isArray(parsed.entitiesToCreate) ? parsed.entitiesToCreate : [];
  const rules = Array.isArray(parsed.rulesToCreate) ? parsed.rulesToCreate : [];

  return {
    runId,
    storyTitle: typeof parsed.storyTitle === "string" ? parsed.storyTitle.trim() : undefined,
    summary: typeof parsed.summary === "string" ? parsed.summary.trim() : `Decomposed ${entities.length} entities and ${fileTypes.length} types`,
    fileTypesToCreate: fileTypes.map((type: any, index: number) => ({
      id: String(type.id || `custom.type_${index + 1}`),
      name: String(type.name || `Type ${index + 1}`),
      designation: String(type.designation || type.id || `type_${index + 1}`),
      description: String(type.description || ""),
      fields: Array.isArray(type.fields) ? type.fields : [{ id: "description", label: "Description", type: "long_text" }],
      isNew: true,
    })),
    entitiesToCreate: entities.map((entity: any, index: number) => {
      let fileTypeId = String(entity.fileTypeId || "").trim();
      // Smart infer domain into Craft core types when missing or defaulted to core.character
      if (!fileTypeId || fileTypeId === "core.character") {
        const titleAndSum = `${entity.title || ""} ${entity.summary || ""} ${JSON.stringify(entity.fields || "")}`.toLowerCase();
        if (/rule|law|system|mechanic|core\s*system|กฎ|ระบบ|ข้อบังคับ/i.test(titleAndSum)) {
          fileTypeId = "core.rule";
        } else if (/monster|beast|creature|boss|อสูร|สัตว์ประหลาด|มอนสเตอร์/i.test(titleAndSum)) {
          fileTypeId = "core.monster";
        } else if (/location|place|continent|dungeon|city|zone|realm|ทวีป|สถานที่|เมือง|ดินแดน/i.test(titleAndSum)) {
          fileTypeId = "core.location";
        } else if (/item|weapon|gear|equipment|armor|shield|ไอเทม|อาวุธ|อุปกรณ์/i.test(titleAndSum)) {
          fileTypeId = "core.item";
        } else if (/race|civilization|faction|guild|tribe|เผ่า|องค์กร|กิลด์|อารยธรรม/i.test(titleAndSum)) {
          fileTypeId = "core.faction";
        } else if (/quest|mission|ภารกิจ|เควสต์/i.test(titleAndSum)) {
          fileTypeId = "core.quest";
        } else if (/card|grimoire|rarity|joker|การ์ด|กริมมัวร์/i.test(titleAndSum)) {
          fileTypeId = "custom.card";
        } else if (/economy|currency|coin|gold|เศรษฐกิจ|เหรียญ|ค่าเงิน/i.test(titleAndSum)) {
          fileTypeId = "custom.economy";
        } else if (/history|lore|ตำนาน|ประวัติศาสตร์/i.test(titleAndSum)) {
          fileTypeId = "core.lore";
        } else {
          fileTypeId = entity.fileTypeId || "core.character";
        }
      }

      return {
        id: String(entity.id || `entity_${index + 1}`),
        fileTypeId,
        title: String(entity.title || `Entity ${index + 1}`),
        summary: String(entity.summary || ""),
        fields: entity.fields && typeof entity.fields === "object" && !Array.isArray(entity.fields) ? entity.fields : {},
        references: Array.isArray(entity.references) ? entity.references : [],
        imagePrompt: typeof entity.imagePrompt === "string" ? entity.imagePrompt : undefined,
        sourceExcerpt: typeof entity.sourceExcerpt === "string" ? entity.sourceExcerpt : undefined,
      };
    }),
    rulesToCreate: rules.map((rule: any, index: number) => {
      const isGmInstruction = /gm|game\s*master|system\s*instruction|narrator|ผู้ดำเนินเรื่อง/i.test(
        `${rule.title || ""} ${rule.content || ""}`
      );
      return {
        id: String(rule.id || `rule_${index + 1}`),
        title: String(rule.title || `Rule ${index + 1}`),
        content: String(rule.content || ""),
        pinned: Boolean(rule.pinned || isGmInstruction),
        triggers: Array.isArray(rule.triggers) ? rule.triggers.map(String) : [],
      };
    }),
  };
}
